/**
 * Design-system reference registry.
 *
 * Single source of truth for every page under `/design-system`. It powers:
 * - the docs shell sidebar navigation and breadcrumbs,
 * - the ⌘K search index,
 * - the overview landing grid,
 * - `scripts/build-design-system-doc.ts`, which serializes this registry (plus
 *   the example sources it references) into `docs/DESIGN-SYSTEM.md` for agents.
 *
 * Keep this module pure data with zero imports so the doc-generator script can
 * import it directly with bun, outside of Vite.
 */

export type DesignSystemGroupId =
  | "foundations"
  | "components"
  | "patterns"
  | "meta";

export type DesignSystemNavIconId =
  | "swatch"
  | "text"
  | "layers"
  | "shape"
  | "icons"
  | "motion"
  | "cursor"
  | "forms"
  | "selection"
  | "feedback"
  | "table"
  | "overlay"
  | "navigation"
  | "chat"
  | "shell"
  | "wallet"
  | "search"
  | "agent";

export interface DesignSystemPageMeta {
  /** URL segment under /design-system/. */
  readonly slug: string;
  readonly title: string;
  /**
   * "wip" marks pages whose components are still being reworked — rendered
   * as a WIP tag in the nav, page header, overview, and generated doc.
   */
  readonly status?: "wip";
  readonly group: DesignSystemGroupId;
  /** One-liner used in nav, search, overview cards, and the generated doc. */
  readonly description: string;
  /** Extra ⌘K search terms beyond title/components. */
  readonly keywords: readonly string[];
  /** Exported component names documented on the page. */
  readonly components: readonly string[];
  /** Repo paths (webapp-v2 relative) documented by the page. */
  readonly sources: readonly string[];
  readonly iconId: DesignSystemNavIconId;
}

export const DESIGN_SYSTEM_GROUP_LABELS: Record<DesignSystemGroupId, string> = {
  foundations: "Foundations",
  components: "Components",
  patterns: "Patterns",
  meta: "Meta",
};

export const DESIGN_SYSTEM_PAGES: readonly DesignSystemPageMeta[] = [
  // Foundations
  {
    slug: "colors",
    title: "Colors",
    group: "foundations",
    description:
      "Semantic tokens, sidebar tokens, and the extended Glider palette — every color the app is allowed to use.",
    keywords: ["tokens", "palette", "semantic", "css variables", "theme"],
    components: [],
    sources: [
      "src/styles/app.css",
      "src/styles/generated-chart-colors.css",
      "src/lib/glider-color-tokens.ts",
      "src/lib/chart-color-system.ts",
    ],
    iconId: "swatch",
  },
  {
    slug: "typography",
    title: "Typography",
    group: "foundations",
    description:
      "Font families, the role-based type ramp, and the typeset prose system that styles rendered markdown.",
    keywords: ["font", "typeset", "prose", "markdown", "type ramp", "text"],
    components: ["Markdown"],
    sources: [
      "src/styles/typeset.css",
      "src/components/prompt-kit/markdown.tsx",
    ],
    iconId: "text",
  },
  {
    slug: "surfaces",
    title: "Surfaces & Elevation",
    group: "foundations",
    description:
      "The surface-level ladder, contextual substrates, selected/hover states, and easing gradients.",
    keywords: [
      "elevation",
      "surface levels",
      "substrate",
      "glider-inset",
      "action surface",
      "overlay surface",
      "bg-hover",
      "bg-active",
    ],
    components: [],
    sources: ["src/lib/surface-classes.ts", "src/lib/surface-level.ts"],
    iconId: "layers",
  },
  {
    slug: "shape",
    title: "Shape & Shadow",
    group: "foundations",
    description:
      "Radius scale, shadow scale, and the light-mode outer-ring outline rules for panels.",
    keywords: ["radius", "border-radius", "shadows", "rounded", "outline"],
    components: [],
    sources: ["src/styles/app.css"],
    iconId: "shape",
  },
  {
    slug: "icons",
    title: "Icons",
    group: "foundations",
    description:
      "The central-icons facade — the only allowed icon import path — with a searchable gallery of every icon.",
    keywords: ["central-icons", "CentralIcon", "iconography", "glyph"],
    components: ["CentralIcon"],
    sources: ["src/components/icons/central-icons.tsx"],
    iconId: "icons",
  },
  {
    slug: "motion",
    title: "Motion",
    group: "foundations",
    description:
      "Fluid spring tiers, overlay motion, animated numbers, and the bat loader — with live sandboxes.",
    keywords: [
      "fluidSprings",
      "animation",
      "framer-motion",
      "spring",
      "LazyMotion",
      "AnimatedNumber",
      "BatShimmer",
    ],
    components: ["AnimatedNumber", "BatShimmer"],
    sources: [
      "src/components/ui/fluid-motion.ts",
      "src/components/animated-number.tsx",
      "src/components/ui/bat-shimmer.tsx",
    ],
    iconId: "motion",
  },
  // Components
  {
    slug: "buttons",
    title: "Buttons",
    group: "components",
    description:
      "Button variants, sizes, icon and loading states, plus the branded glass selection control.",
    keywords: [
      "Button",
      "GlassOptionButton",
      "action",
      "cta",
      "icon button",
      "glass",
      "loading",
      "selection",
    ],
    components: ["Button", "GlassOptionButton"],
    sources: [
      "src/components/ui/button.tsx",
      "src/components/ui/glass-option-button.tsx",
      "src/components/ui/glass-option-button.css",
    ],
    iconId: "cursor",
  },
  {
    slug: "forms",
    title: "Forms & Inputs",
    group: "components",
    description:
      "Text inputs, textareas, number fields, sliders, and the Field composition system with validation states.",
    keywords: [
      "Input",
      "Textarea",
      "NumberField",
      "Field",
      "FieldSet",
      "InputField",
      "InputCopy",
      "Slider",
      "ColorPicker",
      "validation",
    ],
    components: [
      "Input",
      "Textarea",
      "Field",
      "FieldSet",
      "NumberField",
      "InputField",
      "InputCopy",
      "Slider",
      "ColorPicker",
    ],
    sources: [
      "src/components/ui/input.tsx",
      "src/components/ui/textarea.tsx",
      "src/components/ui/field.tsx",
      "src/components/ui/number-field.tsx",
      "src/components/ui/input-group.tsx",
      "src/components/ui/input-copy.tsx",
      "src/components/ui/slider.tsx",
      "src/components/ui/color-picker.tsx",
    ],
    iconId: "forms",
  },
  {
    slug: "selection",
    title: "Selection Controls",
    group: "components",
    description:
      "Checkboxes, radios, switches, selects, comboboxes — every way a user picks from options.",
    keywords: [
      "Checkbox",
      "CheckboxGroup",
      "RadioGroup",
      "RadioItem",
      "Switch",
      "Select",
      "NativeSelect",
      "LabeledValueSelect",
      "inline select",
      "Combobox",
    ],
    components: [
      "Checkbox",
      "CheckboxGroup",
      "RadioGroup",
      "Switch",
      "Select",
      "NativeSelect",
      "LabeledValueSelect",
      "Combobox",
    ],
    sources: [
      "src/components/ui/checkbox.tsx",
      "src/components/ui/checkbox-group.tsx",
      "src/components/ui/radio-group.tsx",
      "src/components/ui/switch.tsx",
      "src/components/ui/select.tsx",
      "src/components/ui/native-select.tsx",
      "src/components/ui/labeled-value-select.tsx",
      "src/components/ui/combobox.tsx",
    ],
    iconId: "selection",
  },
  {
    slug: "feedback",
    title: "Feedback & Status",
    group: "components",
    description:
      "Badges, alerts, status pills, progress, skeletons, and empty states — how the app reports state.",
    keywords: [
      "Badge",
      "Alert",
      "StatusAlert",
      "StatusPill",
      "StatusDot",
      "Progress",
      "ProgressSteps",
      "ProgressRing",
      "Skeleton",
      "EmptyState",
      "IconBadge",
      "toast",
    ],
    components: [
      "Badge",
      "Alert",
      "StatusAlert",
      "StatusPill",
      "IconBadge",
      "Progress",
      "ProgressSteps",
      "ProgressRing",
      "Skeleton",
      "EmptyState",
    ],
    sources: [
      "src/components/ui/badge.tsx",
      "src/components/ui/alert.tsx",
      "src/components/status-alert.tsx",
      "src/components/status-pill.tsx",
      "src/components/icon-badge.tsx",
      "src/components/ui/progress.tsx",
      "src/components/ui/progress-ring.tsx",
      "src/components/ui/skeleton.tsx",
      "src/components/empty-state.tsx",
    ],
    iconId: "feedback",
  },
  {
    slug: "data-display",
    title: "Data Display & Tokens",
    group: "components",
    description:
      "The product DataTable, low-level tables, cards, accordions, avatars, token icons, and scroll areas.",
    keywords: [
      "DataTable",
      "TanStack Table",
      "holdings",
      "Table",
      "DataTableShell",
      "Card",
      "SectionCard",
      "MetricCard",
      "PortfolioHeroCard",
      "Accordion",
      "Avatar",
      "TokenIcon",
      "StackedTokenIcons",
      "ScrollArea",
      "Separator",
    ],
    components: [
      "DataTable",
      "Table",
      "DataTableShell",
      "SectionCard",
      "MetricCard",
      "PortfolioHeroCard",
      "Accordion",
      "Avatar",
      "TokenIcon",
      "StackedTokenIcons",
      "ScrollArea",
      "Separator",
    ],
    sources: [
      "src/components/data-table.tsx",
      "src/components/data-table-presets.ts",
      "src/components/ui/table.tsx",
      "src/components/data-table-shell.tsx",
      "src/components/section-card.tsx",
      "src/components/ui/card.tsx",
      "src/components/ui/portfolio-hero-card.tsx",
      "src/components/ui/portfolio-hero-card.css",
      "src/components/ui/accordion.tsx",
      "src/components/ui/avatar.tsx",
      "src/components/token-icon.tsx",
      "src/components/stacked-token-icons.tsx",
      "src/components/ui/scroll-area.tsx",
      "src/components/ui/separator.tsx",
    ],
    iconId: "table",
  },
  {
    slug: "overlays",
    title: "Overlays",
    group: "components",
    description:
      "Dialogs, alert dialogs, sheets, popovers, dropdown menus, and tooltips on the fluid surface ladder.",
    keywords: [
      "Dialog",
      "AlertDialog",
      "Sheet",
      "Popover",
      "DropdownMenu",
      "Tooltip",
      "modal",
      "drawer",
    ],
    components: [
      "Dialog",
      "AlertDialog",
      "Sheet",
      "Popover",
      "DropdownMenu",
      "Tooltip",
    ],
    sources: [
      "src/components/ui/dialog.tsx",
      "src/components/ui/alert-dialog.tsx",
      "src/components/ui/sheet.tsx",
      "src/components/ui/popover.tsx",
      "src/components/ui/dropdown-menu.tsx",
      "src/components/ui/tooltip.tsx",
      "src/components/ui/fluid-motion.ts",
    ],
    iconId: "overlay",
  },
  {
    slug: "navigation",
    title: "Navigation",
    group: "components",
    description:
      "Command palette, segmented controls, subtle tabs, and navigation menus.",
    keywords: [
      "Command",
      "CommandDialog",
      "cmd-k",
      "SegmentedControl",
      "TabsSubtle",
      "NavigationMenu",
      "tabs",
    ],
    components: [
      "Command",
      "CommandDialog",
      "SegmentedControl",
      "TabsSubtle",
      "NavigationMenu",
    ],
    sources: [
      "src/components/ui/command.tsx",
      "src/components/segmented-control.tsx",
      "src/components/ui/tabs.tsx",
      "src/components/ui/navigation-menu.tsx",
    ],
    iconId: "navigation",
  },
  {
    slug: "ai-chat",
    title: "AI & Chat",
    status: "wip",
    group: "components",
    description:
      "Chat messages, thinking indicators, streaming steps, and the agent input composer.",
    keywords: [
      "ChatMessage",
      "ThinkingIndicator",
      "ThinkingOrb",
      "ThinkingSteps",
      "InputMessage",
      "assistant",
      "agent ui",
    ],
    components: [
      "ChatMessage",
      "ThinkingIndicator",
      "ThinkingOrb",
      "ThinkingSteps",
      "InputMessage",
    ],
    sources: [
      "src/components/ui/chat-message.tsx",
      "src/components/ui/thinking-indicator.tsx",
      "src/components/ui/thinking-orb/",
      "src/components/ui/thinking-steps.tsx",
      "src/components/ui/input-message.tsx",
    ],
    iconId: "chat",
  },
  // Patterns
  {
    slug: "app-shell",
    title: "App Shell",
    group: "patterns",
    description:
      "The real product chrome — sidebar rail, utility dock, notifications, and editor header — on fixtures.",
    keywords: [
      "AppSidebar",
      "DesktopUtilityDock",
      "shell",
      "rail",
      "chrome",
      "layout",
      "notifications",
    ],
    components: ["AppSidebar", "DesktopUtilityDock"],
    sources: ["src/features/app-shell"],
    iconId: "shell",
  },
  {
    slug: "portfolio",
    title: "Portfolio Surfaces",
    group: "patterns",
    description:
      "Portfolio cards, metric grids, and activity tables composed the way product pages compose them.",
    keywords: ["PortfolioCard", "MetricCard", "dashboard", "activity"],
    components: ["PortfolioCard", "MetricCard"],
    sources: [
      "src/features/portfolio-shell",
      "src/features/design-system/ui/metric-card.tsx",
    ],
    iconId: "wallet",
  },
  {
    slug: "asset-selector",
    title: "Asset Selector",
    group: "patterns",
    description:
      "The shared asset search modal in sandbox mode, with deterministic suggestions and results.",
    keywords: ["AssetSelectorModal", "search", "asset picker", "token search"],
    components: ["AssetSelectorModal"],
    sources: ["src/features/asset-selector"],
    iconId: "search",
  },
  // Meta
  {
    slug: "agents",
    title: "For Agents",
    group: "meta",
    description:
      "The rules of the system in one place: import seams, motion, surfaces, i18n, and the scanner tests that enforce them.",
    keywords: [
      "conventions",
      "rules",
      "scanner",
      "claude",
      "llm",
      "contributing",
      "how to add a component",
    ],
    components: [],
    sources: ["AGENTS.md", "src/features/design-system/registry.ts"],
    iconId: "agent",
  },
];

const PAGES_BY_SLUG = new Map(
  DESIGN_SYSTEM_PAGES.map((page) => [page.slug, page]),
);

export function getDesignSystemPage(
  slug: string,
): DesignSystemPageMeta | undefined {
  return PAGES_BY_SLUG.get(slug);
}

export function getDesignSystemPagesByGroup(
  group: DesignSystemGroupId,
): readonly DesignSystemPageMeta[] {
  return DESIGN_SYSTEM_PAGES.filter((page) => page.group === group);
}

export const DESIGN_SYSTEM_NAV_GROUPS: readonly DesignSystemGroupId[] = [
  "foundations",
  "components",
  "patterns",
  "meta",
];

/**
 * Legacy `/design-system?section=` values from the pre-reference single-page
 * layout, mapped to their closest new page. `all` maps to the overview route.
 */
export const LEGACY_SECTION_SLUGS: Record<string, string | null> = {
  all: null,
  tokens: "colors",
  primitives: "buttons",
  motion: "motion",
  overlays: "overlays",
  shells: "app-shell",
};
